import qs from 'qs';
import { PaymentRequestSourceType } from '../redux/payment_methods';

export const safeRedirect = (url: string, fallback: string = '/dashboard') => {
  try {
    const target = new URL(url, window.location.origin);
    if (
      target.protocol !== 'http:' &&
      target.protocol !== 'https:' &&
      target.protocol !== 'mailto:'
    ) {
      throw new Error('Invalid protocol');
    }
    return target.toString();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return new URL(fallback, window.location.origin).toString();
  }
};

/**
 * Parameters for redirecting to the Add Card page.
 *
 * Redirect behaviour is determined by which of successUrl and returnUrl are passed:
 *
 * - successUrl: Where to send the user after they successfully add a card. Used only for the
 *   post-success redirect. May contain the literal placeholder `{paymentMethodId}`; it will be
 *   replaced with the new payment method id when present.
 *
 * - returnUrl: Where the "Back" button on the Add Card page goes. If returnUrl is passed, the
 *   Back button is shown and navigates to this URL. If successUrl is not passed, returnUrl is
 *   also used as the post-success redirect (so success and Back go to the same place).
 *
 * Scenarios:
 *
 * 1. Template + Back (e.g. dashboard PaymentList): Pass both successUrl (templated, e.g.
 *    `.../payment/{paymentMethodId}`) and returnUrl (e.g. list URL). Success → successUrl with
 *    id replaced; Back → returnUrl.
 *
 * 2. Success only, no Back (e.g. payment request flow): Pass successUrl only. Success → successUrl;
 *    no Back button.
 *
 * 3. Templated success, no Back (future): Pass successUrl only (templated). Same as 2.
 *
 * 4. Same URL for success and Back: Pass returnUrl only. Success and Back both use returnUrl.
 */
export type AddCardRedirectParams = {
  /** The retailer ID (required) */
  retailerId: string;
  /**
   * URL to redirect to after the user successfully adds a card.
   * If present, used only for the success redirect. May contain `{paymentMethodId}` to be
   * replaced with the new payment method id. If absent and returnUrl is present, returnUrl
   * is used for the success redirect instead (scenario 4).
   */
  successUrl?: string;
  /**
   * URL the "Back" button on the Add Card page navigates to. When present, the Back button
   * is shown. When successUrl is absent, returnUrl is also used as the post-success redirect.
   * When absent, no Back button is shown (successUrl alone = success redirect only).
   */
  returnUrl?: string;
  /** The purchaser ID to associate the card with */
  purchaserId?: string;
  /** Supplier ID for branded header display */
  supplierId?: string;
  /** Show toggle to set as default for all purchasers */
  hasDefaultForAllToggle?: boolean;
  /** Payment request ID for unauthenticated flows */
  paymentRequestId?: string;
  /** Authentication token for unauthenticated flows */
  token?: string;
  /** Whether user can switch to direct debit (payment request flow) */
  canSwitchToDirect?: boolean;
  /** Source of the payment request - determines if payment request expires ('outstanding-invoices' vs 'payment-request') */
  source?: PaymentRequestSourceType;
};

/** Base path for the Add Card page */
const ADD_CARD_BASE_PATH = '/payments/cards/retailer';

/**
 * Builds the URL for the Add Card page.
 * Use this when you need the URL string (e.g., for <Link> components).
 *
 * @example
 * const url = buildAddCardUrl({ retailerId: '123', purchaserId: '456' });
 * // Returns: '/payments/cards/retailer/123/add?purchaserId=456'
 */
export const buildAddCardUrl = (params: AddCardRedirectParams): string => {
  const { retailerId, ...queryParams } = params;

  // Filter out undefined/null values
  const filteredParams = Object.fromEntries(
    Object.entries(queryParams).filter(([, value]) => value != null)
  );

  const query = qs.stringify(filteredParams);
  return `${ADD_CARD_BASE_PATH}/${retailerId}/add${query ? `?${query}` : ''}`;
};

/**
 * Redirects to the Add Card page using window.location.href.
 * Use this for immediate navigation to the PCI-compliant card entry page.
 *
 * Redirect and Back behaviour on the Add Card page is controlled by successUrl and returnUrl;
 * see AddCardRedirectParams for the four scenarios (template+back, success-only, templated
 * success no-back, returnUrl-only for both success and back).
 *
 * @param params - Redirect parameters for the Add Card page.
 * @param params.retailerId - The retailer ID (required).
 * @param params.successUrl - URL to redirect to after the user successfully adds a card. If set, used only for the success redirect. May contain `{paymentMethodId}` to be replaced with the new payment method id. If omitted and returnUrl is set, returnUrl is used for the success redirect instead.
 * @param params.returnUrl - URL the "Back" button on the Add Card page navigates to. When set, the Back button is shown. When successUrl is omitted, returnUrl is also used as the post-success redirect. When omitted, no Back button is shown.
 * @param params.purchaserId - The purchaser ID to associate the card with.
 * @param params.supplierId - Supplier ID for branded header display on the Add Card page.
 * @param params.hasDefaultForAllToggle - When true, shows the toggle to set the card as default for all purchasers.
 * @param params.paymentRequestId - Payment request ID for unauthenticated (payment request) flows.
 * @param params.token - Authentication token for unauthenticated flows.
 * @param params.canSwitchToDirect - When true, shows the option to switch to direct debit (payment request flow only).
 * @param params.source - Source of the payment request: 'outstanding-invoices' or 'payment-request'. Affects whether the payment request can expire.
 *
 * @example
 * // Scenario 1: Dashboard – templated success URL + Back to list
 * redirectToAddCard({
 *   retailerId: '123',
 *   successUrl: `/dashboard/settings/venues/123/payment-methods/payment/{paymentMethodId}`,
 *   returnUrl: `/dashboard/settings/venues/123/payment-methods`,
 *   hasDefaultForAllToggle: true,
 * });
 *
 * @example
 * // Scenario 2 / 3: Payment request – success redirect only, no Back button
 * redirectToAddCard({
 *   retailerId: '123',
 *   successUrl: `/payments/123/auth?token=...`,
 *   purchaserId: '456',
 *   paymentRequestId: 'pr-789',
 *   token: 'auth-token',
 * });
 *
 * @example
 * // Scenario 4: Same URL for success and Back (e.g. checkout, supplier modal)
 * redirectToAddCard({
 *   retailerId: '123',
 *   purchaserId: '456',
 *   returnUrl: window.location.href,
 * });
 */
export const redirectToAddCard = (params: AddCardRedirectParams): void => {
  const url = buildAddCardUrl(params);
  window.location.href = safeRedirect(url);
};
